/**
 * Credentials admin — SQL/KV-backed.
 *
 * Prior version seeded local mock data. This one pulls from
 * `/api/credentials` via TanStack Query (see ./api.ts) and writes
 * through `POST` / `PUT` / `DELETE`. Responses never contain raw
 * passwords or TOTP seeds; the username is masked server-side.
 *
 * Visual design follows design-reference/ prototype: grouped by
 * portal, rotation-status chips, add/rotate/delete modals. Loading +
 * empty + error states per Rule Zero #8.
 */
import { useMemo, useState } from "react";
import { KeyRound, Plus, RotateCw, Trash2, X, AlertCircle, Loader2 } from "lucide-react";

import { useEntities } from "@/lib/hooks/useEntities";
import { StatusPill } from "@/components/ui/StatusPill";
import { ApiError } from "@/lib/apiClient";

import {
  Credential,
  CreateCredentialPayload,
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useSuggestPassword,
  useUpdateCredential,
} from "./api";
import { AddCredentialModal } from "./AddCredentialModal";
import { isPortalKey, portalLabel } from "./credentialForm";


/** Readable message for a failed `POST /api/credentials`. */
function createErrorMessage(error: unknown): string | null {
  if (!error) return null;
  const ref = error instanceof ApiError && error.correlationId ? ` Reference: ${error.correlationId}` : "";
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return `You don't have access to add credentials for this entity. This needs the entity admin or global admin role.${ref}`;
    }
    if (error.status === 400 && error.message === "unknown_entity") return "That entity does not exist.";
    if (error.status === 429) return "Too many requests. Wait a minute, then try again.";
  }
  return `The credential was not saved. Try again.${ref}`;
}


function groupBy<T, K extends string>(items: T[], key: (t: T) => K): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const t of items) {
    const k = key(t);
    (out[k] ||= []).push(t);
  }
  return out;
}


function rotationChip(c: Credential): { label: string; kind: "overdue" | "soon" | "ok" } {
  if (c.rotationOverdue) {
    const days = c.daysToRotation ?? 0;
    return { label: `Overdue ${Math.abs(days)}d`, kind: "overdue" };
  }
  const days = c.daysToRotation ?? 0;
  if (days <= 14) return { label: `${days}d to rotate`, kind: "soon" };
  return { label: `${days}d to rotate`, kind: "ok" };
}


function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const deltaSec = (Date.now() - t) / 1000;
  if (deltaSec < 90) return "just now";
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86_400) return `${Math.round(deltaSec / 3600)}h ago`;
  if (deltaSec < 86_400 * 7) return `${Math.round(deltaSec / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString();
}


export function CredentialsScreen() {
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [rotateId, setRotateId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useCredentials(
    entityFilter === "all" ? undefined : { entity: entityFilter },
  );
  const createCred = useCreateCredential();
  const entitiesQuery = useEntities();
  const entities = entitiesQuery.data?.items ?? [];
  const deleteCred = useDeleteCredential();

  const creds = useMemo(() => data?.items ?? [], [data]);
  const grouped = useMemo(() => groupBy(creds, (c) => c.portalName), [creds]);
  const stats = useMemo(() => {
    const overdue = creds.filter((c) => c.rotationOverdue).length;
    const soon = creds.filter((c) => !c.rotationOverdue && (c.daysToRotation ?? 0) <= 14).length;
    return { total: creds.length, overdue, soon };
  }, [creds]);

  const flashMessage = (text: string) => {
    setFlash(text);
    window.setTimeout(() => setFlash((f) => (f === text ? null : f)), 3500);
  };

  const handleDelete = (id: string) => {
    deleteCred.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null);
        flashMessage("Credential removed.");
      },
      onError: (err) => flashMessage(`Delete failed: ${(err as Error).message}`),
    });
  };

  const handleCreate = (payload: CreateCredentialPayload) => {
    createCred.mutate(payload, {
      onSuccess: () => {
        setAddOpen(false);
        flashMessage(`Added credential for ${portalLabel(payload.portal_name)}.`);
      },
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-line bg-paper-0 px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-ink-1">Credentials</h1>
          <p className="text-xs text-ink-3">
            {stats.total} total · {stats.overdue} overdue · {stats.soon} due soon
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1"
            aria-label="Filter by entity"
          >
            <option value="all">All entities</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 hover:bg-ink-0"
          >
            <Plus size={14} />
            Add credential
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && (
          <div className="flex items-center gap-2 py-12 text-ink-3">
            <Loader2 className="animate-spin" size={18} />
            Loading credentials…
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-start gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm">
            <AlertCircle className="text-status-failed" size={18} />
            <div>
              <div className="font-medium text-ink-1">Couldn't load credentials.</div>
              <div className="text-ink-3">{(error as Error).message}</div>
              {error instanceof ApiError && error.correlationId && (
                <div className="mt-1 font-mono text-[10px] text-ink-3">{error.correlationId}</div>
              )}
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-2 rounded-md border border-paper-line bg-paper-0 px-2 py-1 text-xs"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && creds.length === 0 && (
          <div className="rounded-md border border-dashed border-paper-line p-8 text-center text-ink-3">
            <KeyRound className="mx-auto mb-2" size={24} />
            No credentials yet. Add one to unblock a workflow.
          </div>
        )}

        {!isLoading && !error && creds.length > 0 && (
          <div className="space-y-5">
            {Object.entries(grouped).map(([portal, items]) => (
              <section key={portal}>
                <h2 className="mb-2 text-sm font-medium text-ink-2">
                  {portalLabel(portal)}{" "}
                  <code className="font-mono text-[11px] text-ink-3">{portal}</code>
                </h2>
                {!isPortalKey(portal) && (
                  <p className="mb-2 text-xs text-status-failed">
                    This portal name is not a portal key. Workflows match the key exactly, so
                    these logins will not be found. Remove and add them again with the key.
                  </p>
                )}
                <ul className="divide-y divide-paper-line rounded-md border border-paper-line bg-paper-0">
                  {items.map((c) => {
                    const chip = rotationChip(c);
                    return (
                      <li key={c.id} className="flex items-center justify-between px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-ink-1">
                            {c.usernameMasked}
                            <span className="ml-2 rounded-sm bg-paper-1 px-1.5 py-0.5 text-[10px] text-ink-3">
                              {c.entityCode}
                            </span>
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
                            <span>MFA: {c.mfaType}</span>
                            <span>Rotated {formatRelative(c.lastRotatedAt)}</span>
                            <span>Last used {formatRelative(c.lastUsedAt)}</span>
                          </div>
                        </div>
                        <div className="ml-3 flex items-center gap-2">
                          <StatusPill
                            status={
                              chip.kind === "overdue"
                                ? "failed"
                                : chip.kind === "soon"
                                  ? "approval"
                                  : "success"
                            }
                            label={chip.label}
                          />
                          <button
                            type="button"
                            onClick={() => setRotateId(c.id)}
                            className="rounded-md border border-paper-line bg-paper-0 p-1.5 text-ink-2 hover:bg-paper-1"
                            aria-label="Rotate"
                          >
                            <RotateCw size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(c.id)}
                            className="rounded-md border border-paper-line bg-paper-0 p-1.5 text-ink-2 hover:bg-paper-1"
                            aria-label="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>

      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 rounded-md bg-ink-1 px-4 py-2 text-sm text-paper-0 shadow-lg"
        >
          {flash}
        </div>
      )}

      {addOpen && (
        <AddCredentialModal
          onClose={() => {
            createCred.reset();
            setAddOpen(false);
          }}
          onSubmit={handleCreate}
          submitting={createCred.isPending}
          error={createErrorMessage(createCred.error)}
        />
      )}

      {rotateId && (
        <RotateModal
          credentialId={rotateId}
          onClose={() => setRotateId(null)}
          onDone={(msg) => {
            setRotateId(null);
            flashMessage(msg);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDeleteModal
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => handleDelete(pendingDelete)}
          submitting={deleteCred.isPending}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Modals
// ──────────────────────────────────────────────────────────────

function RotateModal(props: {
  credentialId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const update = useUpdateCredential(props.credentialId);
  const suggest = useSuggestPassword(props.credentialId);
  const [pw, setPw] = useState("");
  const [totp, setTotp] = useState("");

  const fillSuggested = () => {
    suggest.mutate(undefined, {
      onSuccess: (r) => setPw(r.suggested),
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: { password?: string; totp_secret?: string } = {};
    if (pw) payload.password = pw;
    if (totp) payload.totp_secret = totp;
    if (!payload.password && !payload.totp_secret) {
      props.onDone("Nothing to rotate.");
      return;
    }
    update.mutate(payload, {
      onSuccess: () => props.onDone("Credential rotated."),
      onError: (err) => props.onDone(`Rotate failed: ${(err as Error).message}`),
    });
  };

  return (
    <ModalFrame title="Rotate credential" onClose={props.onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-ink-3">
          Paste the new value(s) you set in the payer portal. Leave blank to keep a field unchanged.
        </p>
        <Field label="New password">
          <input
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className={INPUT_CLS}
            type="password"
            autoComplete="new-password"
          />
        </Field>
        <button
          type="button"
          onClick={fillSuggested}
          className="text-xs text-ink-3 underline"
        >
          Generate a strong password
        </button>
        <Field label="New TOTP seed (base32, if MFA=totp)">
          <input
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            className={INPUT_CLS}
            autoComplete="off"
          />
        </Field>
        {(update.error || suggest.error) && (
          <div className="rounded-md bg-status-failed/10 p-2 text-xs text-status-failed">
            {((update.error || suggest.error) as Error).message}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
          >
            {update.isPending && <Loader2 className="animate-spin" size={14} />}
            Save
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

function ConfirmDeleteModal(props: {
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <ModalFrame title="Remove credential?" onClose={props.onCancel}>
      <p className="text-sm text-ink-2">
        The row will drop and the KV secret will be soft-deleted (KV's purge-protection keeps 90 days of history).
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={props.onCancel}
          className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={props.onConfirm}
          disabled={props.submitting}
          className="inline-flex items-center gap-1 rounded-md bg-status-failed px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
        >
          {props.submitting && <Loader2 className="animate-spin" size={14} />}
          Remove
        </button>
      </div>
    </ModalFrame>
  );
}

// ──────────────────────────────────────────────────────────────
// Shared form primitives
// ──────────────────────────────────────────────────────────────

const INPUT_CLS = "w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1";

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-ink-3">
      <span>{props.label}</span>
      <div className="mt-1">{props.children}</div>
    </label>
  );
}

function ModalFrame(props: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/30 p-4">
      <div className="w-full max-w-md rounded-lg border border-paper-line bg-paper-0 p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-1">{props.title}</h3>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-3 hover:bg-paper-1"
          >
            <X size={14} />
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}
